/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { COMPARABLE_CRAWLS, CRAWL_DIFF } from "../../gql/queries/crawlDiff";
import { formatDate } from "./InnerHeader";
import PageDiffDialog from "./PageDiffDialog";

/**
 * What changed between this report and another one of the same site.
 *
 * The comparison is made when it is asked for, from the page content both
 * crawls stored, so any two reports can be put side by side rather than only
 * consecutive ones.
 */

const PAGE_SIZE = 25;

const Wrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  "& .controls": {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  "& .counts": {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  "& .urlCell": {
    wordBreak: "break-all",
    cursor: "pointer",
    color: theme.palette.primary.main,
    "&:hover": { textDecoration: "underline" },
  },
}));

/** How each status is worded and coloured for the reader. */
const STATUS = {
  changed: { label: "Changed", color: "warning" },
  new: { label: "New", color: "info" },
  removed: { label: "Removed", color: "error" },
  same: { label: "Unchanged", color: "default" },
  "not-compared": { label: "Not compared", color: "default" },
};

function ContentChangesTab({ sessionId, projectId }) {
  const [compareTo, setCompareTo] = useState("");
  const [status, setStatus] = useState("changed");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [openRow, setOpenRow] = useState(null);

  // Only the reports this one can actually be compared with. A report crawled
  // before page content was stored differs on every page at once, which says
  // nothing about the site, so it is never offered.
  const { data: listData, loading: listLoading } = useQuery(COMPARABLE_CRAWLS, {
    variables: { sessionId },
    skip: !sessionId,
  });

  const sessions = useMemo(() => listData?.ComparableCrawls?.crawls || [], [listData]);
  // Why there is nothing to offer, when the API knows -- a report crawled
  // before page content was stored cannot be compared with anything, which is
  // a different thing from being the first report of its site.
  const noneReason = listData?.ComparableCrawls?.reason;

  // Default to the most recent of them, which is the report crawled just
  // before this one and what the reader almost always wants.
  useEffect(() => {
    if (compareTo || !sessions.length) return;
    // A report produced the same way gives a full comparison, so prefer one
    // even if a more recent report exists that was produced differently.
    setCompareTo((sessions.find(x => x.sameBasis) || sessions[0]).sessionId);
  }, [sessions, compareTo]);

  // Nothing to compare with -- either genuinely nothing (first report of
  // this site) or a stored-differently reason from the API. Known as soon as
  // the comparable-crawls list resolves, well before the page-list query
  // below is even allowed to run.
  const nothingToCompare = !listLoading && !sessions.length;

  // Corrected here, ahead of the page-list query, rather than after seeing
  // its response: firing that query once with a status nothing can match
  // returns an empty page list, which reads as "no pages" for the instant
  // before this same correction would otherwise arrive.
  useEffect(() => {
    if (nothingToCompare && (status === "changed" || status === "same")) {
      setStatus("all");
      setPage(1);
    }
  }, [nothingToCompare, status]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Waits on the comparable-crawls list rather than on `compareTo` alone:
  // that is what lets the `nothingToCompare` correction above land before
  // this query ever fires, instead of racing it.
  const { data, loading, error } = useQuery(CRAWL_DIFF, {
    variables: {
      sessionId,
      compareTo: compareTo || undefined,
      status,
      search: debounced || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    skip: !sessionId || listLoading,
    fetchPolicy: "cache-and-network",
  });

  const result = data?.CrawlDiff;
  const summary = result?.summary;
  const contentComparable = summary ? summary.comparable !== false : true;

  useEffect(() => {
    if (summary && summary.comparable === false && (status === "changed" || status === "same")) {
      setStatus("all");
      setPage(1);
    }
  }, [summary, status]);

  if (listLoading && !sessions.length) {
    return (
      <Wrapper>
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {nothingToCompare && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
            {noneReason ? "There is nothing to compare this report with yet." : "This is the only report of this site so far."}
          </Typography>
          {noneReason ? (
            <>
              {noneReason.charAt(0).toUpperCase() + noneReason.slice(1)}. Reports crawled from now on will store it, and the first
              two of those can be compared with each other. Its pages are listed below.
            </>
          ) : (
            <>Once a second report finishes, its changes against this one will show up here. For now, here is everything this crawl found.</>
          )}
        </Alert>
      )}

      <Box className="controls">
        {!nothingToCompare && (
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="compare-with-label">Compare with report</InputLabel>
            <Select labelId="compare-with-label" label="Compare with report" value={compareTo} onChange={e => { setCompareTo(e.target.value); setPage(1); }}>
              {sessions.map(s => (
                <MenuItem key={s.sessionId} value={s.sessionId}>
                  {formatDate(s.createdAt)} — {s.pages} pages
                  {s.sameBasis === false ? " (stored differently)" : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="status-label">Show</InputLabel>
          <Select labelId="status-label" label="Show" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
            {/* Kept in the list rather than removed: taking the selected
                option away leaves the field holding a value it no longer
                offers, which renders blank for a frame and warns. */}
            <MenuItem value="changed" disabled={!contentComparable}>
              Changed
            </MenuItem>
            <MenuItem value="new" disabled={nothingToCompare}>
              New
            </MenuItem>
            <MenuItem value="removed" disabled={nothingToCompare}>
              Removed
            </MenuItem>
            <MenuItem value="same" disabled={!contentComparable}>
              Unchanged
            </MenuItem>
            <MenuItem value="all">All pages</MenuItem>
          </Select>
        </FormControl>

        <TextField size="small" label="Filter by url" value={search} onChange={e => setSearch(e.target.value)} sx={{ minWidth: 220 }} />
      </Box>

      {loading && !result && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {error && <Alert severity="error">Could not compare these reports: {error.message}</Alert>}

      {summary && !summary.comparable && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {summary.reason}
        </Alert>
      )}

      {summary && summary.partialCoverage && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {summary.coverageReason}
        </Alert>
      )}

      {summary && (
        <Box className="counts">
          {contentComparable && <Chip color="warning" label={`${summary.changed} changed`} />}
          {!nothingToCompare && <Chip color="info" label={`${summary.new} new`} />}
          {!nothingToCompare && <Chip color="error" label={`${summary.removed} removed`} />}
          {contentComparable && <Chip label={`${summary.same} unchanged`} />}
          {nothingToCompare ? (
            <Chip variant="outlined" label={`${summary.total} pages found`} />
          ) : (
            <Tooltip title="Pages this report audited, against the report it is compared with">
              <Chip variant="outlined" label={`${summary.total} pages vs ${summary.previousTotal}`} />
            </Tooltip>
          )}
        </Box>
      )}

      {result && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>URL</TableCell>
                <TableCell width={140}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.pages.map(row => (
                <TableRow key={row.url} hover>
                  <TableCell className="urlCell" onClick={() => setOpenRow(row)}>
                    {row.url}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={STATUS[row.status]?.color || "default"} label={STATUS[row.status]?.label || row.status} />
                  </TableCell>
                </TableRow>
              ))}
              {!result.pages.length && (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
                      {debounced ? "No url matches that filter." : "Nothing in this category."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {result.total > PAGE_SIZE && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination count={Math.ceil(result.total / PAGE_SIZE)} page={page} onChange={(e, v) => setPage(v)} size="small" />
            </Box>
          )}
        </>
      )}

      <PageDiffDialog
        open={!!openRow}
        onClose={() => setOpenRow(null)}
        sessionId={sessionId}
        compareTo={compareTo}
        url={openRow?.url}
      />
    </Wrapper>
  );
}

export default ContentChangesTab;
